import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlogStore } from '../stores/blogStore';
import { Button } from '../components/common/Button';
import { BlogTag } from '../types/blog';
import { toast } from 'react-toastify';
import { Edit2, Trash2, Tag, Loader2 } from 'lucide-react';
import ConfirmDialog from '../components/common/ConfirmDialog';

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }

  return 'Unknown error';
};

const BlogTags: React.FC = () => {
  const { t } = useTranslation('admin');
  const { tags, fetchTags, createTag, updateTag, deleteTag } = useBlogStore();
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<BlogTag>>({ name: '', slug: '' });

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleCreateOrUpdate = async () => {
    if (!formData.name || !formData.slug) {
      toast.error(t('blogAdmin.required_fields'));
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await updateTag(isEditing, formData);
        toast.success(t('blogAdmin.tag_updated'));
        setIsEditing(null);
      } else {
        await createTag(formData);
        toast.success(t('blogAdmin.tag_created'));
      }
      setFormData({ name: '', slug: '' });
    } catch (error: unknown) {
      toast.error(`${t('blogAdmin.save_error')}: ${getErrorMessage(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tag: BlogTag) => {
    setIsEditing(tag.id);
    setFormData(tag);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setDeleteLoading(true);
    try {
      await deleteTag(deleteId);
      toast.success(t('blogAdmin.tag_deleted'));
      setDeleteId(null);
    } catch (error: unknown) {
      toast.error(`${t('blogAdmin.save_error')}: ${getErrorMessage(error)}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-primary/10 text-primary rounded-2xl">
            <Tag size={32} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('navigation.tags')}</h1>
            <p className="text-sm text-gray-500">{t('blogAdmin.manage_tags_hint')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Form */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm h-fit space-y-4">
            <h3 className="font-bold text-gray-900">
              {isEditing ? t('blogAdmin.edit_tag') : t('blogAdmin.create_tag')}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">
                  {t('blogAdmin.name')}
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                  value={formData.name}
                  onChange={e => {
                    const name = e.target.value;
                    const slug = name
                      .toLowerCase()
                      .replace(/\s+/g, '-')
                      .replace(/[^\w-]/g, '');
                    setFormData({ ...formData, name, slug });
                  }}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">
                  {t('blogAdmin.slug')}
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-gray-50"
                  value={formData.slug}
                  onChange={e => setFormData({ ...formData, slug: e.target.value })}
                />
              </div>
              <Button onClick={handleCreateOrUpdate} disabled={loading} className="w-full mt-2">
                {loading && <Loader2 size={16} className="animate-spin mr-2" />}
                {isEditing ? t('blogAdmin.update') : t('blogAdmin.add')}
              </Button>
              {isEditing && (
                <Button
                  variant="simple-text"
                  onClick={() => {
                    setIsEditing(null);
                    setFormData({ name: '', slug: '' });
                  }}
                  className="w-full"
                >
                  {t('blogAdmin.cancel')}
                </Button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="md:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[500px]">
                <thead className="bg-gray-50 border-b border-gray-100 font-bold text-gray-400 text-xs uppercase">
                  <tr>
                    <th className="p-4">{t('blogAdmin.name')}</th>
                    <th className="p-4">{t('blogAdmin.slug')}</th>
                    <th className="p-4 text-right">{t('table_fields.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tags.map(tag => (
                    <tr key={tag.id} className="hover:bg-gray-50 transition-colors group">
                      <td className="p-4">
                        <div className="font-semibold text-gray-900">{tag.name}</div>
                      </td>
                      <td className="p-4 text-sm text-gray-500 font-mono italic">{tag.slug}</td>
                      <td className="p-4 text-right space-x-2">
                        <button
                          onClick={() => handleEdit(tag)}
                          className="p-2 text-gray-400 hover:text-primary transition-colors"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteId(tag.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title={t('blogAdmin.confirm_delete_tag')}
        message={
          t('blogAdmin.confirm_delete_tag_hint') || 'Voulez-vous vraiment supprimer ce mot-clé ?'
        }
      />
    </>
  );
};

export default BlogTags;
